export const JOBS = [
  {
    title: "Robotics Software Engineer",
    meta: "United States | Remote-friendly | Full-time / Contract",
    summary:
      "Build the software layer that turns multimodal physical data into reliable robot-ready datasets, evaluation loops, and deployment workflows.",
    responsibilities: [
      "Develop robotics data pipelines for human demonstration, robot execution, sensor streams, and task outcome labels.",
      "Integrate perception, calibration, trajectory processing, and dataset validation workflows across Actrium capture systems.",
      "Build internal tools for reviewing robot trials, diagnosing failures, and improving dataset quality over time.",
      "Work closely with AI, hardware, and operations teams to move physical AI systems from prototype to field deployment.",
    ],
    requirements: [
      "Strong Python or C++ engineering experience with production-quality software habits.",
      "Hands-on robotics experience with ROS, manipulation, perception, simulation, controls, or robot data infrastructure.",
      "Comfort working with messy real-world sensor data and debugging systems that combine hardware and software.",
      "Bonus: experience with embodied AI, imitation learning, teleoperation, SLAM, 3D vision, or cloud data platforms.",
    ],
  },
  {
    title: "Robotics Domain Expert",
    meta: "United States | Remote-friendly | Part-time / Contract",
    summary:
      "Help define high-quality robotic task data, evaluate physical AI behavior, and guide the standards Actrium uses for real-world robot learning.",
    responsibilities: [
      "Design evaluation criteria for manipulation, navigation, contact-rich tasks, and failure recovery scenarios.",
      "Review robot execution traces and identify whether failures come from perception, planning, control, data quality, or task ambiguity.",
      "Create task rubrics, annotation guides, and expert feedback that improve dataset consistency.",
      "Advise the team on realistic robotics benchmarks, deployment constraints, and field-readiness signals.",
    ],
    requirements: [
      "Deep practical knowledge of robotics, embodied AI, robot learning, controls, or manipulation.",
      "Ability to explain complex robot behavior clearly and turn expert judgment into repeatable review standards.",
      "Experience with real robots, simulation environments, lab operations, or deployment testing.",
      "Bonus: publications, open-source work, or industry experience in robot foundation models or data-centric AI.",
    ],
  },
  {
    title: "Multimodal Data Annotation Specialist",
    meta: "United States | Remote-friendly | Contract",
    summary:
      "Annotate and structure real physical interaction data so AI teams can train models on motion, force, contact, intent, and outcomes.",
    responsibilities: [
      "Label human demonstrations, robot task videos, sensor timelines, action phases, errors, and success states.",
      "Follow detailed annotation rubrics while flagging unclear, inconsistent, or low-confidence examples.",
      "Compare video, trajectory, and sensor signals to produce clean training-ready metadata.",
      "Collaborate with engineers to improve labeling tools, guidelines, and quality checks.",
    ],
    requirements: [
      "Excellent attention to detail and ability to work consistently across repeated review tasks.",
      "Comfort interpreting video, motion, sensor, or time-series data in a structured workflow.",
      "Clear written communication and willingness to document edge cases.",
      "Bonus: background in robotics, data labeling, QA, biomechanics, manufacturing, kitchens, labs, or operations.",
    ],
  },
  {
    title: "Visual Data Quality Specialist",
    meta: "United States | Remote-friendly | Contract",
    summary:
      "Review visual datasets for robotics and embodied AI, focusing on object states, human actions, environment context, and model-relevant errors.",
    responsibilities: [
      "Inspect image and video data for framing, occlusion, object identity, hand-object interaction, and task progression.",
      "Identify visual edge cases that could affect robot learning, evaluation, or deployment reliability.",
      "Validate annotations against source media and provide concise correction notes.",
      "Help maintain quality standards for datasets used in robotics model training and benchmarking.",
    ],
    requirements: [
      "Strong visual judgment and careful review habits.",
      "Experience with image, video, or computer vision data workflows.",
      "Ability to apply guidelines consistently while escalating ambiguous cases.",
      "Bonus: familiarity with CV annotation, robotics tasks, action recognition, or data QA tooling.",
    ],
  },
  {
    title: "Physical AI Data Operations Specialist",
    meta: "United States | Hybrid / Remote-friendly | Full-time / Contract",
    summary:
      "Coordinate the operational workflow behind Actrium datasets, from capture planning and quality review to delivery readiness.",
    responsibilities: [
      "Manage dataset intake, labeling queues, review milestones, and delivery checklists for robotics data projects.",
      "Coordinate with capture teams, annotators, engineers, and customers to keep data workflows moving cleanly.",
      "Track data quality issues, documentation gaps, and operational blockers across projects.",
      "Improve repeatable processes for U.S.-based data handling, privacy-aware review, and customer delivery.",
    ],
    requirements: [
      "Experience in data operations, technical project coordination, AI operations, robotics labs, or dataset production.",
      "Strong organization, follow-through, and written communication.",
      "Comfort working with technical teams and translating project needs into clear operational tasks.",
      "Bonus: experience with robotics, ML datasets, annotation teams, QA workflows, or customer-facing delivery.",
    ],
  },
];
